import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';
import { EMPLOYEE_ROUTES, TenantService } from '@kuiper/ibusiness-core';
import { ContractService } from '../contract.service';
import { ContractDetails } from '../contract-details.model';
import { ContractTypesEnum } from '../contract-type.enum';

export interface ContractDetailsResolvedData {
  contractDetails: ContractDetails;
  contractType: string;
}

export const contractDetailsResolver: ResolveFn<
  ContractDetailsResolvedData | RedirectCommand
> = route => {
  const contractService = inject(ContractService);
  const router = inject(Router);
  const tenantService = inject(TenantService);

  const projectId = route.paramMap.get('projectId') ?? '';
  const contractId = route.paramMap.get('contractId') ?? '';

  const redirectCommand = new RedirectCommand(
    router.parseUrl(
      tenantService.tenantPath(
        `${EMPLOYEE_ROUTES.PROJECTS.DETAILS}/${projectId}`
      )
    )
  );

  if (!projectId || !contractId) {
    return redirectCommand;
  }

  const redirectToProject = () => of(redirectCommand);

  return contractService.getContractById(projectId, contractId).pipe(
    switchMap(response => {
      const contractType =
        response.data.type?.value ?? ContractTypesEnum.CONTRACTOR;

      if (contractType === ContractTypesEnum.DEVELOPER) {
        return contractService.getDeveloperContract(+projectId).pipe(
          map(developerResponse => ({
            contractDetails: enrichContractDetails(developerResponse.data),
            contractType,
          }))
        );
      }

      return of({
        contractDetails: enrichContractDetails(response.data),
        contractType,
      });
    }),
    catchError(() => redirectToProject())
  );
};

/** Applies derived fields that the details screen expects on the model. */
export function enrichContractDetails(
  details: ContractDetails
): ContractDetails {
  const totalContractAmountWithTax =
    calculateTotalContractAmountWithTax(details);

  const roundedTotalContractAmountWithTax =
    Math.floor(totalContractAmountWithTax * 100) / 100;

  return {
    ...details,
    total_contract_amount_with_tax:
      roundedTotalContractAmountWithTax.toString(),
  };
}

function calculateTotalContractAmountWithTax(details: ContractDetails): number {
  if (!details.tax_type?.is_active) {
    return 0;
  }

  const amount = +details.amount;
  const taxRate = +details.tax_type.percentage / 100;
  const taxMultiplier = details.tax_type.is_calculated_in_total ? 1 : 0;

  return amount + amount * taxRate * taxMultiplier;
}
